import React, { useState } from 'react';
import { 
  Shield, 
  AlertTriangle, 
  Info, 
  XCircle, 
  CheckCircle,
  ExternalLink,
  Copy,
  Download,
  Upload,
  Eye,
  ChevronDown,
  ChevronRight,
  Globe,
  Server,
  FileText,
  Brain
} from 'lucide-react';

const ScannerResults = ({ result, tool, onExportToVulnerabilities, onDownload }) => {
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [selectedItem, setSelectedItem] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);

  if (!result || !result.data) {
    return (
      <div style={{ 
        padding: '24px', 
        textAlign: 'center', 
        color: 'var(--text-secondary)' 
      }}>
        No results available
      </div>
    );
  }

  const data = result.data || [];
  const summary = result.summary || {};

  const getToolIcon = (toolName) => {
    switch (toolName) {
      case 'nuclei':
        return <Shield size={16} />;
      case 'httpx':
        return <Globe size={16} />;
      case 'subfinder':
        return <Server size={16} />;
      case 'katana':
        return <FileText size={16} />;
      case 'ai_assistant':
        return <Brain size={16} />;
      case 'x8':
        return <AlertTriangle size={16} />;
      default:
        return <Shield size={16} />;
    }
  };

  const getToolColor = (toolName) => {
    switch (toolName) {
      case 'nuclei':
        return '#8b5cf6';
      case 'httpx':
        return '#3b82f6';
      case 'subfinder':
        return '#10b981';
      case 'katana':
        return '#f59e0b';
      case 'ai_assistant':
        return '#a855f7';
      case 'x8':
        return '#ec4899';
      default:
        return '#6b7280';
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'critical':
        return '#dc2626';
      case 'high':
        return '#ea580c';
      case 'medium':
        return '#f59e0b';
      case 'low':
        return '#10b981';
      case 'info':
        return '#3b82f6';
      default:
        return '#6b7280';
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'critical':
      case 'high':
        return <XCircle size={16} />;
      case 'medium':
        return <AlertTriangle size={16} />;
      case 'low':
      case 'info':
        return <Info size={16} />;
      default:
        return <Shield size={16} />;
    }
  };

  const toggleExpanded = (index) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedItems(newExpanded);
  };

  const handleViewDetails = (item) => {
    setSelectedItem(item);
    setShowDetailModal(true);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  // Render different content based on tool type
  const renderToolSpecificContent = () => {
    switch (tool) {
      case 'nuclei':
        return renderNucleiResults();
      case 'httpx':
        return renderHttpxResults();
      case 'subfinder':
        return renderSubfinderResults();
      case 'katana':
        return renderKatanaResults();
      case 'ai_assistant':
        return renderAIAssistantResults();
      case 'x8':
        return renderX8Results();
      default:
        return renderGenericResults();
    }
  };

  const renderNucleiResults = () => {
    return (
      <div style={{ 
        border: '1px solid var(--border-color)', 
        borderRadius: '8px', 
        overflow: 'hidden' 
      }}>
        {data.length === 0 ? (
          <div style={{ 
            padding: '48px', 
            textAlign: 'center', 
            color: 'var(--text-secondary)' 
          }}>
            <Shield size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
            <div>No vulnerabilities found</div>
          </div>
        ) : (
          data.map((vuln, index) => (
            <div key={index} style={{ 
              borderBottom: index < data.length - 1 ? '1px solid var(--border-color)' : 'none',
              backgroundColor: 'var(--bg-secondary)'
            }}>
              <div style={{ 
                padding: '16px', 
                display: 'flex', 
                alignItems: 'center', 
                gap: '12px',
                cursor: 'pointer'
              }} onClick={() => toggleExpanded(index)}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px',
                  color: getSeverityColor(vuln.severity),
                  minWidth: '120px'
                }}>
                  {getSeverityIcon(vuln.severity)}
                  <span style={{ fontWeight: '600', textTransform: 'capitalize' }}>
                    {vuln.severity}
                  </span>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                    {vuln.name || vuln.template_id || 'Unknown Vulnerability'}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                    {vuln.host} {vuln.matched_at && `• ${vuln.matched_at}`}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewDetails(vuln);
                    }}
                    style={{
                      padding: '4px 8px',
                      border: '1px solid #3b82f6',
                      borderRadius: '4px',
                      backgroundColor: 'transparent',
                      color: '#3b82f6',
                      cursor: 'pointer',
                      fontSize: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    <Eye size={12} />
                    Details
                  </button>
                  {expandedItems.has(index) ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </div>
              </div>

              {expandedItems.has(index) && (
                <div style={{ 
                  padding: '0 16px 16px 16px', 
                  backgroundColor: 'var(--bg-tertiary)',
                  borderTop: '1px solid var(--border-color)'
                }}>
                  <div style={{ marginBottom: '12px' }}>
                    <strong>Template ID:</strong> {vuln.template_id}
                  </div>
                  {vuln.type && (
                    <div style={{ marginBottom: '12px' }}>
                      <strong>Type:</strong> {vuln.type}
                    </div>
                  )}
                  {vuln.matched_at && (
                    <div style={{ marginBottom: '12px' }}>
                      <strong>Matched At:</strong> {vuln.matched_at}
                    </div>
                  )}
                  {vuln.host && (
                    <div style={{ marginBottom: '12px' }}>
                      <strong>Host:</strong> {vuln.host}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    );
  };

  const renderHttpxResults = () => {
    return (
      <div style={{ 
        border: '1px solid var(--border-color)', 
        borderRadius: '8px', 
        overflow: 'hidden' 
      }}>
        {data.length === 0 ? (
          <div style={{ 
            padding: '48px', 
            textAlign: 'center', 
            color: 'var(--text-secondary)' 
          }}>
            <Globe size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
            <div>No live hosts found</div>
          </div>
        ) : (
          data.map((host, index) => (
            <div key={index} style={{ 
              borderBottom: index < data.length - 1 ? '1px solid var(--border-color)' : 'none',
              backgroundColor: 'var(--bg-secondary)',
              padding: '16px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px',
                  color: getToolColor('httpx')
                }}>
                  {getToolIcon('httpx')}
                  <span style={{ fontWeight: '600' }}>Live Host</span>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                    {host.url}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                    Status: {host.status_code} • Length: {host.content_length} • Title: {host.title}
                  </div>
                </div>
                <button
                  onClick={() => handleViewDetails(host)}
                  style={{
                    padding: '4px 8px',
                    border: '1px solid #3b82f6',
                    borderRadius: '4px',
                    backgroundColor: 'transparent',
                    color: '#3b82f6',
                    cursor: 'pointer',
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  <Eye size={12} />
                  Details
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    );
  };

  const renderSubfinderResults = () => {
    return (
      <div style={{ 
        border: '1px solid var(--border-color)', 
        borderRadius: '8px', 
        overflow: 'hidden' 
      }}>
        {data.length === 0 ? (
          <div style={{ 
            padding: '48px', 
            textAlign: 'center', 
            color: 'var(--text-secondary)' 
          }}>
            <Server size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
            <div>No subdomains found</div>
          </div>
        ) : (
          <div style={{ padding: '16px' }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              color: getToolColor('subfinder'),
              marginBottom: '16px'
            }}>
              {getToolIcon('subfinder')}
              <span style={{ fontWeight: '600' }}>Discovered Subdomains</span>
            </div>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', 
              gap: '8px' 
            }}>
              {data.map((subdomain, index) => (
                <div key={index} style={{
                  padding: '8px 12px',
                  backgroundColor: 'var(--bg-tertiary)',
                  borderRadius: '4px',
                  fontSize: '14px',
                  fontFamily: 'monospace'
                }}>
                  {subdomain}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderKatanaResults = () => {
    return (
      <div style={{ 
        border: '1px solid var(--border-color)', 
        borderRadius: '8px', 
        overflow: 'hidden' 
      }}>
        {data.length === 0 ? (
          <div style={{ 
            padding: '48px', 
            textAlign: 'center', 
            color: 'var(--text-secondary)' 
          }}>
            <FileText size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
            <div>No URLs discovered</div>
          </div>
        ) : (
          <div style={{ padding: '16px' }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              color: getToolColor('katana'),
              marginBottom: '16px'
            }}>
              {getToolIcon('katana')}
              <span style={{ fontWeight: '600' }}>Discovered URLs</span>
            </div>
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '8px',
              maxHeight: '400px',
              overflow: 'auto'
            }}>
              {data.map((url, index) => (
                <div key={index} style={{
                  padding: '8px 12px',
                  backgroundColor: 'var(--bg-tertiary)',
                  borderRadius: '4px',
                  fontSize: '14px',
                  fontFamily: 'monospace',
                  wordBreak: 'break-all'
                }}>
                  {url}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderX8Results = () => {
    return (
      <div style={{ 
        border: '1px solid var(--border-color)', 
        borderRadius: '8px', 
        overflow: 'hidden' 
      }}>
        {data.length === 0 ? (
          <div style={{ 
            padding: '48px', 
            textAlign: 'center', 
            color: 'var(--text-secondary)' 
          }}>
            <AlertTriangle size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
            <div>No reflected parameters found</div>
          </div>
        ) : (
          data.map((item, index) => {
            const isExpanded = expandedItems.has(index);
            const reflectedParams = item.reflected_params || [];
            
            return (
              <div key={index} style={{ 
                borderBottom: index < data.length - 1 ? '1px solid var(--border-color)' : 'none',
                backgroundColor: 'var(--bg-secondary)'
              }}>
                <div style={{ 
                  padding: '16px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '12px',
                  cursor: 'pointer'
                }} onClick={() => toggleExpanded(index)}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px',
                    color: getToolColor('x8'),
                    flex: 1
                  }}>
                    {getToolIcon('x8')}
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '600', marginBottom: '4px', wordBreak: 'break-all' }}>
                        {item.url}
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                        Method: {item.method || 'GET'} • 
                        Status: {item.status || 'N/A'} • 
                        Size: {item.size || 'N/A'} • 
                        Reflected Params: {reflectedParams.length}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewDetails(item);
                    }}
                    style={{
                      padding: '4px 8px',
                      border: '1px solid #ec4899',
                      borderRadius: '4px',
                      backgroundColor: 'transparent',
                      color: '#ec4899',
                      cursor: 'pointer',
                      fontSize: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    <Eye size={12} />
                    Details
                  </button>
                  {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </div>

                {isExpanded && (
                  <div style={{ 
                    padding: '0 16px 16px 16px', 
                    backgroundColor: 'var(--bg-tertiary)',
                    borderTop: '1px solid var(--border-color)'
                  }}>
                    {reflectedParams.length > 0 ? (
                      <div>
                        <div style={{ 
                          fontWeight: '600', 
                          marginBottom: '12px',
                          color: '#ec4899'
                        }}>
                          Reflected Parameters ({reflectedParams.length})
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {reflectedParams.map((param, paramIndex) => (
                            <div key={paramIndex} style={{
                              padding: '12px',
                              backgroundColor: 'var(--bg-secondary)',
                              borderRadius: '4px',
                              border: '1px solid var(--border-color)'
                            }}>
                              <div style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '8px',
                                marginBottom: '8px'
                              }}>
                                <span style={{ 
                                  fontWeight: '600',
                                  color: '#ec4899',
                                  fontSize: '14px'
                                }}>
                                  {param.name}
                                </span>
                                <span style={{ 
                                  fontSize: '12px', 
                                  color: 'var(--text-secondary)'
                                }}>
                                  Status: {param.status || 'N/A'} • Size: {param.size || 'N/A'}
                                </span>
                              </div>
                              {param.diffs && (
                                <div style={{ 
                                  fontSize: '11px', 
                                  fontFamily: 'monospace',
                                  color: 'var(--text-secondary)',
                                  marginTop: '4px',
                                  padding: '4px',
                                  backgroundColor: 'var(--bg-tertiary)',
                                  borderRadius: '2px'
                                }}>
                                  Diffs: {param.diffs}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
                        No reflected parameters found for this URL
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    );
  };

  const renderAIAssistantResults = () => {
    const metadata = result.metadata || {};
    
    return (
      <div>
        {/* Summary Card */}
        <div style={{
          backgroundColor: '#a855f715',
          border: '1px solid #a855f7',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '16px',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '12px',
          }}>
            <div style={{ fontSize: '24px' }}>🧠</div>
            <div>
              <div style={{ fontSize: '16px', fontWeight: '600', color: '#a855f7' }}>
                AI Assistant Results
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                Model: {metadata.model_used || 'Unknown'} • 
                Processed: {metadata.output_items || 0} items
                {metadata.chunks_processed > 1 && ` • Chunks: ${metadata.chunks_processed}`}
              </div>
            </div>
          </div>
          
          {/* Prompt Preview */}
          {metadata.prompt && (
            <div style={{ marginTop: '12px' }}>
              <button
                onClick={() => setShowPrompt(!showPrompt)}
                style={{
                  fontSize: '12px',
                  color: '#a855f7',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                  marginBottom: '8px',
                }}
              >
                {showPrompt ? '▼' : '▶'} Prompt
              </button>
              {showPrompt && (
                <div style={{
                  backgroundColor: 'var(--bg-tertiary)',
                  padding: '12px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  fontFamily: 'monospace',
                  whiteSpace: 'pre-wrap',
                  maxHeight: '200px',
                  overflow: 'auto',
                }}>
                  {metadata.prompt}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Results List */}
        <div style={{
          border: '1px solid var(--border-color)',
          borderRadius: '8px',
          overflow: 'hidden',
        }}>
          {data.length === 0 ? (
            <div style={{
              padding: '48px',
              textAlign: 'center',
              color: 'var(--text-secondary)',
            }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>🧠</div>
              <div>No results from AI processing</div>
            </div>
          ) : (
            data.map((item, index) => {
              const isExpanded = expandedItems.has(index);
              
              // Generate preview text (first key-value or truncated JSON)
              let preview = '';
              if (typeof item === 'object' && item !== null) {
                const keys = Object.keys(item);
                if (keys.length > 0) {
                  const firstKey = keys[0];
                  preview = `${firstKey}: ${String(item[firstKey]).substring(0, 50)}${String(item[firstKey]).length > 50 ? '...' : ''}`;
                }
              } else {
                preview = String(item).substring(0, 80);
              }

              return (
                <div
                  key={index}
                  style={{
                    borderBottom: index < data.length - 1 ? '1px solid var(--border-color)' : 'none',
                    backgroundColor: 'var(--bg-secondary)',
                  }}
                >
                  {/* Header */}
                  <div
                    style={{
                      padding: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      cursor: 'pointer',
                    }}
                    onClick={() => toggleExpanded(index)}
                  >
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      color: '#a855f7',
                      minWidth: '80px',
                    }}>
                      <Brain size={16} />
                      <span style={{ fontWeight: '600' }}>Item {index + 1}</span>
                    </div>
                    <div style={{ flex: 1, fontSize: '14px', color: 'var(--text-secondary)' }}>
                      {preview}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          copyToClipboard(JSON.stringify(item, null, 2));
                        }}
                        style={{
                          padding: '4px 8px',
                          border: '1px solid #a855f7',
                          borderRadius: '4px',
                          backgroundColor: 'transparent',
                          color: '#a855f7',
                          cursor: 'pointer',
                          fontSize: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                        }}
                      >
                        <Copy size={12} />
                        Copy
                      </button>
                      {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </div>
                  </div>

                  {/* Expanded Content */}
                  {isExpanded && (
                    <div style={{
                      padding: '0 16px 16px 16px',
                      backgroundColor: 'var(--bg-tertiary)',
                      borderTop: '1px solid var(--border-color)',
                    }}>
                      {typeof item === 'object' && item !== null ? (
                        <div>
                          {/* Key-Value Display */}
                          {Object.entries(item).map(([key, value]) => (
                            <div key={key} style={{
                              padding: '8px 0',
                              borderBottom: '1px solid var(--border-color)',
                            }}>
                              <div style={{
                                fontSize: '12px',
                                fontWeight: '600',
                                color: '#a855f7',
                                marginBottom: '4px',
                              }}>
                                {key}
                              </div>
                              <div style={{
                                fontSize: '14px',
                                color: 'var(--text-primary)',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                              }}>
                                {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <pre style={{
                          fontSize: '14px',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          margin: 0,
                        }}>
                          {String(item)}
                        </pre>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    );
  };

  const renderGenericResults = () => {
    return (
      <div style={{ 
        border: '1px solid var(--border-color)', 
        borderRadius: '8px', 
        overflow: 'hidden' 
      }}>
        <div style={{ padding: '16px' }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            color: getToolColor(tool),
            marginBottom: '16px'
          }}>
            {getToolIcon(tool)}
            <span style={{ fontWeight: '600' }}>Results</span>
          </div>
          <pre style={{
            backgroundColor: 'var(--bg-tertiary)',
            padding: '16px',
            borderRadius: '4px',
            overflow: 'auto',
            maxHeight: '400px',
            fontSize: '12px',
            whiteSpace: 'pre-wrap'
          }}>
            {Array.isArray(data) 
              ? (data.length > 0 && typeof data[0] === 'object'
                  ? JSON.stringify(data, null, 2)
                  : data.join('\n'))
              : JSON.stringify(data, null, 2)}
          </pre>
        </div>
      </div>
    );
  };

  return (
    <div style={{ padding: '16px' }}>
      {/* Summary */}
      <div style={{ 
        marginBottom: '24px', 
        padding: '16px', 
        backgroundColor: 'var(--bg-tertiary)', 
        borderRadius: '8px' 
      }}>
        <h3 style={{ marginBottom: '12px', fontSize: '18px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
          {getToolIcon(tool)}
          {tool.toUpperCase()} Scan Summary
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: getToolColor(tool) }}>
              {summary.total_items || data.length}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Total Items</div>
          </div>
          {summary.by_severity && Object.entries(summary.by_severity).map(([severity, count]) => (
            <div key={severity} style={{ textAlign: 'center' }}>
              <div style={{ 
                fontSize: '24px', 
                fontWeight: 'bold', 
                color: getSeverityColor(severity) 
              }}>
                {count}
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)', textTransform: 'capitalize' }}>
                {severity}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div style={{ 
        marginBottom: '16px', 
        display: 'flex', 
        gap: '12px', 
        flexWrap: 'wrap' 
      }}>
        {onExportToVulnerabilities && tool === 'nuclei' && (
          <button
            onClick={onExportToVulnerabilities}
            style={{
              padding: '8px 16px',
              border: '1px solid #8b5cf6',
              borderRadius: '6px',
              backgroundColor: 'transparent',
              color: '#8b5cf6',
              cursor: 'pointer',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#8b5cf6';
              e.currentTarget.style.color = 'white';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = '#8b5cf6';
            }}
          >
            <Upload size={16} />
            Export to Vulnerabilities
          </button>
        )}
        {onDownload && (
          <button
            onClick={onDownload}
            style={{
              padding: '8px 16px',
              border: '1px solid #10b981',
              borderRadius: '6px',
              backgroundColor: 'transparent',
              color: '#10b981',
              cursor: 'pointer',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#10b981';
              e.currentTarget.style.color = 'white';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = '#10b981';
            }}
          >
            <Download size={16} />
            Download Results
          </button>
        )}
      </div>

      {/* Tool-specific results */}
      {renderToolSpecificContent()}

      {/* Detail Modal */}
      {showDetailModal && selectedItem && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }} onClick={() => setShowDetailModal(false)}>
          <div style={{
            backgroundColor: 'var(--bg-secondary)',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '800px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto',
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h2 style={{ fontSize: '20px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px' }}>
                {getToolIcon(tool)}
                {tool.toUpperCase()} Result Details
              </h2>
              <button
                onClick={() => setShowDetailModal(false)}
                style={{
                  padding: '4px 12px',
                  border: 'none',
                  borderRadius: '4px',
                  backgroundColor: '#6b7280',
                  color: 'white',
                  cursor: 'pointer',
                }}
              >
                Close
              </button>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <h3 style={{ fontSize: '18px', marginBottom: '8px' }}>
                {selectedItem.name || selectedItem.url || selectedItem.template_id || 'Result Item'}
              </h3>
            </div>

            {/* Raw Data */}
            <div style={{ marginBottom: '16px' }}>
              <h4 style={{ marginBottom: '8px' }}>Raw Data</h4>
              <div style={{ 
                position: 'relative',
                backgroundColor: 'var(--bg-tertiary)',
                borderRadius: '4px',
                padding: '12px',
                border: '1px solid var(--border-color)'
              }}>
                <button
                  onClick={() => copyToClipboard(JSON.stringify(selectedItem, null, 2))}
                  style={{
                    position: 'absolute',
                    top: '8px',
                    right: '8px',
                    padding: '4px 8px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '4px',
                    backgroundColor: 'var(--bg-secondary)',
                    color: 'var(--text-primary)',
                    cursor: 'pointer',
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  <Copy size={12} />
                  Copy
                </button>
                <pre style={{
                  fontSize: '12px',
                  overflow: 'auto',
                  maxHeight: '300px',
                  margin: 0,
                  whiteSpace: 'pre-wrap'
                }}>
                  {JSON.stringify(selectedItem, null, 2)}
                </pre>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScannerResults;
